import { memo } from 'react';
import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CatalogSortProps {
    sortBy: string;
    sortOrder: string;
    onSortChange: (sortBy: string, sortOrder: string) => void;
}

export const CatalogSort = memo(({ sortBy, sortOrder, onSortChange }: CatalogSortProps) => {
    const sortOptions = [
        { name: 'Featured', sortBy: 'recommended', sortOrder: 'asc' },
        { name: 'Most Popular', sortBy: 'popularity', sortOrder: 'desc' },
        { name: 'Price: Low to High', sortBy: 'price', sortOrder: 'asc' },
        { name: 'Price: High to Low', sortBy: 'price', sortOrder: 'desc' },
        { name: 'Name: A-Z', sortBy: 'name', sortOrder: 'asc' },
    ];

    const currentSort = sortOptions.find(
        (option) => option.sortBy === sortBy && option.sortOrder === sortOrder
    ) || sortOptions[0];

    return (
        <Menu as="div" className="relative inline-block text-left">
            <div>
                <Menu.Button className="group inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-border/80 bg-background px-4 py-2 text-sm font-semibold text-foreground shadow-sm transition-colors hover:bg-secondary">
                    <span className="text-muted-foreground">Sort</span>
                    <span>{currentSort.name}</span>
                    <ChevronDown
                        className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-data-[headlessui-state=open]:rotate-180"
                        aria-hidden="true"
                    />
                </Menu.Button>
            </div>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute right-0 z-20 mt-2 w-52 origin-top-right rounded-2xl border border-border/80 bg-white p-2 shadow-[0_20px_48px_rgba(54,49,43,0.14)] focus:outline-none">
                    {sortOptions.map((option) => (
                        <Menu.Item key={option.name}>
                            {({ active }) => (
                                <button
                                    onClick={() => onSortChange(option.sortBy, option.sortOrder)}
                                    className={cn(
                                        'block w-full rounded-xl px-3 py-2.5 text-left text-sm transition-colors',
                                        option.name === currentSort.name ? 'bg-secondary font-semibold text-foreground' : 'text-muted-foreground',
                                        active && 'bg-secondary/80 text-foreground'
                                    )}
                                >
                                    {option.name}
                                </button>
                            )}
                        </Menu.Item>
                    ))}
                </Menu.Items>
            </Transition>
        </Menu>
    );
});

CatalogSort.displayName = 'CatalogSort';
